/**
 * Style graphique et charte des visuels et supports (recette du dirigeant du 31/10/2026 : « les visuels fonctionnent
 * mais peu stylés […] Apporter de la couleur vive. Définir un style graphique. Donner le choix de définir des styles
 * et une charte graphique au support »).
 *
 *  - Un CATALOGUE de styles (`STYLES`) : chacun = palette nommée (rôles), formes (rayons, ombres, dégradés, relief),
 *    typographies (polices livrées avec le serveur), et un fragment de consigne pour l'illustrateur Gemini.
 *  - Une CHARTE de l'organisme (`SchemaCharte`) : style par défaut, couleurs personnalisées, police, intensité, mode de
 *    rendu, gabarit de support, ajustement permis ou non aux formateurs, plafond d'images par formation.
 *  - Une SURCHARGE par support / formation (`SchemaStyleSupport`) : style, gabarit, mode ; couleurs seulement si
 *    l'organisme l'autorise.
 *  - `resoudreTheme` produit le thème effectif, avec contrôle du contraste WCAG 2.2 (AA : 4,5:1 pour le texte courant,
 *    3:1 pour les grands titres) : une couleur choisie qui ne passe pas est corrigée et l'écart est signalé.
 *
 * Module pur (aucune dépendance au serveur) : partagé par le serveur (rendu SVG, PPTX) et l'interface (sélecteurs).
 */
import { z } from "zod";

// ——— Couleurs : conversions et contraste (WCAG) ———

const HEX = /^#?[0-9a-f]{6}$/i;
export const estHex = (c: unknown): c is string => typeof c === "string" && HEX.test(c);
export const normaliserHex = (c: string) => `#${c.replace("#", "").toUpperCase()}`;

function rgb(c: string): [number, number, number] {
  const n = parseInt(c.replace("#", ""), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const versHex = (r: number, g: number, b: number) => `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("")}`.toUpperCase();

/** Luminance relative WCAG d'une couleur hexadécimale. */
export function luminance(c: string): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = rgb(c);
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** Rapport de contraste WCAG entre deux couleurs (1 à 21). */
export function contraste(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m) as [number, number];
  return (x + 0.05) / (y + 0.05);
}

/** Mélange `c` avec `avec` (part 0 → c, 1 → avec). */
export function melanger(c: string, avec: string, part: number): string {
  const [r1, g1, b1] = rgb(c);
  const [r2, g2, b2] = rgb(avec);
  return versHex(r1 + (r2 - r1) * part, g1 + (g2 - g1) * part, b1 + (b2 - b1) * part);
}
export const eclaircir = (c: string, part: number) => melanger(c, "#FFFFFF", part);
export const assombrir = (c: string, part: number) => melanger(c, "#000000", part);

function versHsl(c: string): [number, number, number] {
  const [r, g, b] = rgb(c).map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s, l];
}
function depuisHsl(h: number, s: number, l: number): string {
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return versHex(f(0) * 255, f(8) * 255, f(4) * 255);
}
/** Saturation multipliée par `facteur` (intensité des couleurs). */
export function saturer(c: string, facteur: number): string {
  const [h, s, l] = versHsl(c);
  return depuisHsl(h, Math.max(0, Math.min(1, s * facteur)), l);
}

/** Couleur de texte lisible sur `fond` : blanc ou `encre`, celle qui contraste le plus. */
export function texteSur(fond: string, encre = "#16163A"): string {
  return contraste("#FFFFFF", fond) >= contraste(encre, fond) ? "#FFFFFF" : encre;
}

/** Assombrit (ou éclaircit sur fond sombre) `c` jusqu'à atteindre `cible`:1 contre `fond`. */
export function ajusterContraste(c: string, fond: string, cible = 4.5): string {
  if (contraste(c, fond) >= cible) return normaliserHex(c);
  const vers = luminance(fond) > 0.35 ? "#000000" : "#FFFFFF";
  for (let i = 1; i <= 20; i++) {
    const essai = melanger(c, vers, i * 0.05);
    if (contraste(essai, fond) >= cible) return essai;
  }
  return vers === "#000000" ? "#000000" : "#FFFFFF";
}

// ——— Catalogue des styles ———

export const ROLES_COULEUR = ["primaire", "secondaire", "accent", "fond", "encre", "succes", "alerte"] as const;
export type RoleCouleur = (typeof ROLES_COULEUR)[number];
export type PaletteNommee = Record<RoleCouleur, string>;

export const LIBELLES_ROLES: Record<RoleCouleur, { libelle: string; role: string }> = {
  primaire: { libelle: "Primaire", role: "titres forts, éléments principaux, bandeaux" },
  secondaire: { libelle: "Secondaire", role: "seconds éléments, liens, dégradés" },
  accent: { libelle: "Accent", role: "chiffres clés, mise en relief, pictogrammes" },
  fond: { libelle: "Fond", role: "fond des visuels et des diapositives" },
  encre: { libelle: "Texte", role: "texte courant (contraste AA obligatoire sur le fond)" },
  succes: { libelle: "État positif", role: "validation, bonnes pratiques" },
  alerte: { libelle: "État d'alerte", role: "vigilance, erreurs à éviter" },
};

export const POLICES = ["Poppins", "Nunito", "Space Grotesk", "Fraunces"] as const;
export type Police = (typeof POLICES)[number];

export type Ombre = "aucune" | "douce" | "portee" | "lueur" | "papier";

export interface StyleVisuel {
  id: IdStyle;
  nom: string;
  description: string;
  palette: PaletteNommee;
  /** Couleurs des éléments successifs (étapes, niveaux, branches) : la variété aide à distinguer et mémoriser. */
  serie: string[];
  sombre: boolean;
  police_titre: Police;
  police_texte: Police;
  rayon: number;
  ombre: Ombre;
  degrade: boolean;
  /** Cartes extrudées (effet 3D isométrique). */
  relief: boolean;
  contour: number;
  /** Fragment de consigne pour l'illustrateur (en anglais : langue de travail recommandée des modèles d'image). */
  prompt: string;
}

export const IDS_STYLES = ["s4m-vif", "iso-3d", "flat", "infographie", "papier", "neon", "editorial", "corporate"] as const;
export type IdStyle = (typeof IDS_STYLES)[number];
export const STYLE_DEFAUT: IdStyle = "s4m-vif";

/** Palette « Skills4mation Vif » (style maison par défaut, documenté dans docs/STYLE_VISUELS.md). */
export const PALETTE_S4M_VIF: PaletteNommee = {
  primaire: "#4F46E5", // Indigo électrique
  secondaire: "#0EA5E9", // Lagon
  accent: "#FF8A00", // Mangue
  fond: "#F7F7FF", // Nuage
  encre: "#16163A", // Nuit
  succes: "#10B981", // Menthe
  alerte: "#F43F5E", // Framboise
};
export const NOMS_PALETTE_S4M: Record<RoleCouleur, string> = { primaire: "Indigo électrique", secondaire: "Lagon", accent: "Mangue", fond: "Nuage", encre: "Nuit", succes: "Menthe", alerte: "Framboise" };

const st = (s: Omit<StyleVisuel, "id"> & { id: IdStyle }) => s;

export const STYLES: Record<IdStyle, StyleVisuel> = {
  "s4m-vif": st({
    id: "s4m-vif",
    nom: "Skills4mation Vif",
    description: "Le style maison : couleurs vives et franches, cartes arrondies à ombre douce, dégradés légers, pictogrammes.",
    palette: PALETTE_S4M_VIF,
    serie: ["#4F46E5", "#0EA5E9", "#FF8A00", "#10B981", "#F43F5E", "#A855F7", "#14B8A6"],
    sombre: false,
    police_titre: "Poppins",
    police_texte: "Poppins",
    rayon: 18,
    ombre: "douce",
    degrade: true,
    relief: false,
    contour: 0,
    prompt: "modern vibrant 3D illustration, soft clay-like rounded objects, glossy highlights, soft studio lighting, cheerful saturated colors, clean light background",
  }),
  "iso-3d": st({
    id: "iso-3d",
    nom: "3D isométrique vif",
    description: "Blocs extrudés en perspective isométrique, couleurs saturées : idéal pour les processus, cycles et pyramides.",
    palette: { primaire: "#6D28D9", secondaire: "#06B6D4", accent: "#F59E0B", fond: "#F4F2FF", encre: "#1E1B3A", succes: "#22C55E", alerte: "#EF4444" },
    serie: ["#6D28D9", "#06B6D4", "#F59E0B", "#22C55E", "#EC4899", "#3B82F6", "#F97316"],
    sombre: false,
    police_titre: "Poppins",
    police_texte: "Poppins",
    rayon: 10,
    ombre: "portee",
    degrade: true,
    relief: true,
    contour: 0,
    prompt: "isometric 3D illustration, 30-degree isometric angle, extruded blocks and miniature objects, vivid saturated colors, soft ambient occlusion, clean pastel background",
  }),
  flat: st({
    id: "flat",
    nom: "Flat design coloré",
    description: "Aplats de couleurs franches, sans ombre, formes géométriques nettes : lisible et énergique.",
    palette: { primaire: "#E11D48", secondaire: "#2563EB", accent: "#FACC15", fond: "#FFFFFF", encre: "#111827", succes: "#16A34A", alerte: "#DC2626" },
    serie: ["#2563EB", "#E11D48", "#F59E0B", "#16A34A", "#9333EA", "#0891B2", "#EA580C"],
    sombre: false,
    police_titre: "Poppins",
    police_texte: "Nunito",
    rayon: 10,
    ombre: "aucune",
    degrade: false,
    relief: false,
    contour: 0,
    prompt: "flat vector illustration, bold solid color shapes, no gradients, geometric simplified characters and objects, crisp edges, white background",
  }),
  infographie: st({
    id: "infographie",
    nom: "Infographie moderne",
    description: "Grands numéros, pastilles et fines lignes, teintes claires relevées d'un accent vif : l'esprit data-design.",
    palette: { primaire: "#0F766E", secondaire: "#7C3AED", accent: "#F97316", fond: "#F8FAFC", encre: "#0F172A", succes: "#059669", alerte: "#E11D48" },
    serie: ["#0F766E", "#7C3AED", "#F97316", "#0284C7", "#DB2777", "#65A30D", "#CA8A04"],
    sombre: false,
    police_titre: "Space Grotesk",
    police_texte: "Nunito",
    rayon: 14,
    ombre: "douce",
    degrade: false,
    relief: false,
    contour: 2,
    prompt: "modern infographic illustration, clean geometric icons and objects, subtle depth, balanced composition, crisp vector-like rendering, light neutral background",
  }),
  papier: st({
    id: "papier",
    nom: "Papier découpé / relief",
    description: "Couches de papier superposées, ombres portées décalées, tons chaleureux : tactile et mémorable.",
    palette: { primaire: "#E4572E", secondaire: "#17BEBB", accent: "#FFC914", fond: "#FFF7EC", encre: "#2E1F1A", succes: "#4C9F38", alerte: "#C0392B" },
    serie: ["#E4572E", "#17BEBB", "#FFC914", "#76B041", "#8E5BD9", "#F08A4B", "#2E86AB"],
    sombre: false,
    police_titre: "Nunito",
    police_texte: "Nunito",
    rayon: 24,
    ombre: "papier",
    degrade: false,
    relief: false,
    contour: 0,
    prompt: "layered paper cut-out craft illustration, stacked paper layers with soft drop shadows, visible paper texture, warm vivid colors, handmade diorama look",
  }),
  neon: st({
    id: "neon",
    nom: "Néon sur fond sombre",
    description: "Fond nuit, traits lumineux et halos : percutant en salle, à réserver aux supports projetés.",
    palette: { primaire: "#22D3EE", secondaire: "#A78BFA", accent: "#F472B6", fond: "#0B1026", encre: "#F1F5FF", succes: "#4ADE80", alerte: "#FB7185" },
    serie: ["#22D3EE", "#F472B6", "#A78BFA", "#4ADE80", "#FACC15", "#FB923C", "#60A5FA"],
    sombre: true,
    police_titre: "Space Grotesk",
    police_texte: "Space Grotesk",
    rayon: 16,
    ombre: "lueur",
    degrade: true,
    relief: false,
    contour: 2.5,
    prompt: "neon glow illustration on a deep navy night background, luminous cyan magenta and violet light lines, futuristic, cinematic contrast, synthwave atmosphere",
  }),
  editorial: st({
    id: "editorial",
    nom: "Illustration éditoriale",
    description: "Esprit magazine : titres à empattement, fond crème, couleurs franches mais posées, personnages stylisés.",
    palette: { primaire: "#B5361C", secondaire: "#1F6F78", accent: "#E9A23B", fond: "#FFF8EE", encre: "#2B2118", succes: "#3E7C3A", alerte: "#B42318" },
    serie: ["#B5361C", "#1F6F78", "#E9A23B", "#5B4B8A", "#3E7C3A", "#C2410C", "#0E7490"],
    sombre: false,
    police_titre: "Fraunces",
    police_texte: "Nunito",
    rayon: 12,
    ombre: "douce",
    degrade: false,
    relief: false,
    contour: 0,
    prompt: "editorial magazine illustration, stylized characters and objects, grainy texture, bold yet refined color palette, conceptual metaphor, cream background",
  }),
  corporate: st({
    id: "corporate",
    nom: "Sobre corporate",
    description: "Bleus profonds et gris, accent unique, beaucoup d'air : pour les clients institutionnels.",
    palette: { primaire: "#1E3A8A", secondaire: "#2563EB", accent: "#0EA5E9", fond: "#FFFFFF", encre: "#0F172A", succes: "#15803D", alerte: "#B91C1C" },
    serie: ["#1E3A8A", "#2563EB", "#0EA5E9", "#334155", "#0F766E", "#6366F1", "#64748B"],
    sombre: false,
    police_titre: "Poppins",
    police_texte: "Nunito",
    rayon: 8,
    ombre: "douce",
    degrade: false,
    relief: false,
    contour: 1.5,
    prompt: "clean corporate 3D illustration, professional business setting, restrained blue palette with one bright accent, soft lighting, minimal background",
  }),
};

// ——— Gabarits de supports (catalogue ; le détail des mises en page est dans domaine/supports/gabarits.ts) ———

export const IDS_GABARITS = ["classique-pro", "moderne-vif", "atelier-pratique", "immersif-3d"] as const;
export type IdGabarit = (typeof IDS_GABARITS)[number];
export const GABARIT_DEFAUT: IdGabarit = "moderne-vif";

export const MODES_RENDU = ["illustre", "vectoriel"] as const;
export type ModeRendu = (typeof MODES_RENDU)[number];
export const LIBELLES_MODE: Record<ModeRendu, string> = { illustre: "Illustré (Gemini)", vectoriel: "Schéma (vectoriel)" };

export const INTENSITES = ["douce", "vive", "intense"] as const;
export type Intensite = (typeof INTENSITES)[number];
const FACTEUR_INTENSITE: Record<Intensite, number> = { douce: 0.72, vive: 1, intense: 1.25 };

// ——— Charte de l'organisme et surcharge par support ———

const couleur = z
  .string()
  .trim()
  .refine((c) => c === "" || HEX.test(c), "Couleur au format #RRGGBB.")
  .transform((c) => (c ? normaliserHex(c) : ""));
const Couleurs = z.object(Object.fromEntries(ROLES_COULEUR.map((r) => [r, couleur.optional()])) as Record<RoleCouleur, z.ZodOptional<typeof couleur>>);

export const PLAFOND_IMAGES_DEFAUT = 25;

export const SchemaCharte = z.object({
  style: z.enum(IDS_STYLES).default(STYLE_DEFAUT),
  couleurs: Couleurs.default({}),
  /** "" = la police du style. */
  police: z.union([z.enum(POLICES), z.literal("")]).default(""),
  intensite: z.enum(INTENSITES).default("vive"),
  mode: z.enum(MODES_RENDU).default("illustre"),
  gabarit: z.enum(IDS_GABARITS).default(GABARIT_DEFAUT),
  /** Les formateurs peuvent ajuster les couleurs de leurs supports (dans les limites du contraste). */
  ajustement_formateur: z.boolean().default(false),
  plafond_images: z.number().int().min(0).max(1000).default(PLAFOND_IMAGES_DEFAUT),
  logo_sur_supports: z.boolean().default(true),
});
export type Charte = z.infer<typeof SchemaCharte>;

export const SchemaStyleSupport = z.object({
  style: z.enum(IDS_STYLES).optional(),
  gabarit: z.enum(IDS_GABARITS).optional(),
  mode: z.enum(MODES_RENDU).optional(),
  couleurs: Couleurs.optional(),
});
export type StyleSupport = z.infer<typeof SchemaStyleSupport>;

/** Charte lue en base (jsonb libre, éventuellement vide ou ancienne) : toujours une charte complète et valide. */
export function lireCharte(brut: unknown): Charte {
  const r = SchemaCharte.safeParse(brut && typeof brut === "object" ? brut : {});
  return r.success ? r.data : SchemaCharte.parse({});
}
export function lireStyleSupport(brut: unknown): StyleSupport {
  const r = SchemaStyleSupport.safeParse(brut && typeof brut === "object" ? brut : {});
  return r.success ? r.data : {};
}

// ——— Thème effectif ———

export interface Theme {
  style: StyleVisuel;
  mode: ModeRendu;
  gabarit: IdGabarit;
  /** Couleurs effectives (après charte, surcharge, intensité et contrôle du contraste). */
  c: PaletteNommee & {
    /** Surface des cartes. */
    surface: string;
    /** Texte secondaire (contraste ≥ 4,5:1 sur le fond). */
    encre_douce: string;
    trait: string;
    /** Primaire assez foncé (ou clair, sur fond sombre) pour porter du texte : ≥ 4,5:1 avec `sur_primaire`. */
    primaire_texte: string;
    sur_primaire: string;
  };
  serie: string[];
  police_titre: string;
  police_texte: string;
  /** Corrections appliquées pour tenir le contraste AA (affichées dans l'écran de charte). */
  corrections: string[];
}

/**
 * Thème effectif d'un support : charte de l'organisme, puis surcharge du support (couleurs seulement si
 * `ajustement_formateur`), intensité, puis garde-fous de contraste (texte ≥ 4,5:1 sur le fond).
 */
export function resoudreTheme(charte: Charte, surcharge: StyleSupport = {}): Theme {
  const style = STYLES[surcharge.style ?? charte.style] ?? STYLES[STYLE_DEFAUT];
  const choisies = { ...nettoyer(charte.couleurs), ...(charte.ajustement_formateur ? nettoyer(surcharge.couleurs ?? {}) : {}) };
  // Les couleurs de la charte ne s'appliquent au style choisi que s'il est de même famille (clair / sombre) que le fond choisi.
  const brute: PaletteNommee = { ...style.palette, ...choisies };
  if (choisies.fond && luminance(choisies.fond) < 0.2 !== style.sombre) brute.fond = style.palette.fond;
  const facteur = FACTEUR_INTENSITE[charte.intensite];
  const vif = (c: string) => (facteur === 1 ? normaliserHex(c) : saturer(c, facteur));
  const corrections: string[] = [];
  const fond = normaliserHex(brute.fond);
  let encre = normaliserHex(brute.encre);
  if (contraste(encre, fond) < 4.5) {
    const corrigee = ajusterContraste(encre, fond, 4.5);
    corrections.push(`Texte ${encre} trop peu contrasté sur le fond ${fond} (${contraste(encre, fond).toFixed(2)}:1) : remplacé par ${corrigee}.`);
    encre = corrigee;
  }
  const c = {
    primaire: vif(brute.primaire),
    secondaire: vif(brute.secondaire),
    accent: vif(brute.accent),
    fond,
    encre,
    succes: vif(brute.succes),
    alerte: vif(brute.alerte),
  };
  const surface = style.sombre ? melanger(fond, "#FFFFFF", 0.07) : "#FFFFFF";
  const encre_douce = ajusterContraste(melanger(encre, fond, 0.3), surface, 4.5);
  const sur_primaire = texteSur(c.primaire, encre);
  const primaire_texte = contraste(c.primaire, sur_primaire) >= 4.5 ? c.primaire : ajusterContraste(c.primaire, sur_primaire, 4.5);
  const serie = (Object.keys(choisies).some((k) => ["primaire", "secondaire", "accent"].includes(k)) ? [c.primaire, c.secondaire, c.accent, ...style.serie.slice(3)] : style.serie).map(vif);
  return {
    style,
    mode: surcharge.mode ?? charte.mode,
    gabarit: surcharge.gabarit ?? charte.gabarit,
    c: { ...c, surface, encre_douce, trait: melanger(fond, encre, 0.16), primaire_texte, sur_primaire },
    serie,
    police_titre: charte.police || style.police_titre,
    police_texte: charte.police || style.police_texte,
    corrections,
  };
}

function nettoyer(c: Partial<Record<RoleCouleur, string | undefined>>): Partial<PaletteNommee> {
  return Object.fromEntries(Object.entries(c).filter(([k, v]) => (ROLES_COULEUR as readonly string[]).includes(k) && estHex(v)).map(([k, v]) => [k, normaliserHex(v!)])) as Partial<PaletteNommee>;
}

/** Couleur de la série pour l'élément `i` (cyclique). */
export const couleurSerie = (t: Theme, i: number) => t.serie[i % t.serie.length]!;

/** Couleur pleine qui porte un texte (blanc ou encre) lisible : renvoie le fond corrigé et la couleur du texte. */
export function pastille(fond: string, encre = "#16163A"): { fond: string; texte: string } {
  const texte = texteSur(fond, encre);
  return { fond: contraste(fond, texte) >= 4.5 ? fond : ajusterContraste(fond, texte, 4.5), texte };
}
